<template>
  <div>
    <div class="VariantsContainer">
      <div v-if="productOptions?.length" class="order-last mt-9.5 lg:mt-5 flex flex-col relative space-y-9.5">
        <div v-for="(attribute, idx) in productOptions" :key="idx" class="mb-7">
          <div class="flex items-center justify-between">
            <i18n-t keypath="choose" tag="div" class="font-body text-sm flex text-black items-center">
              <template #label>
                <span class="text-black ml-1"> {{ attribute.label }} </span>
              </template>

              <template #value>
                <span class="ml-2 font-bold text-black">
                  {{
                    productOptions
                      ?.find(option => option.attribute_code === attribute.attribute_code)
                      ?.values?.find(option => option.uid === selectedOptions?.[attribute?.attribute_code || ''])?.label
                  }}
                </span>
              </template>

              {{ attribute.label }}
            </i18n-t>
            <slot v-if="attribute.attribute_code === 'size'" name="size-cta" />
          </div>
          <div class="mt-4 flex items-center gap-5 flex-wrap">
            <div v-for="(option, optionIdx) in attribute.values" :key="optionIdx">
              <div
                v-if="option.swatch_data && isColor(option.swatch_data.value || '')"
                class="w-8 h-8 relative"
                :disabled="!activeOption(option)"
                :class="{
                  'disabled-option': !activeOption(option),
                }"
              >
                <button
                  class="w-full h-full rounded-full"
                  :style="{ 'background-color': option.swatch_data.value || '' }"
                  :class="{
                    'disabled-option': !activeOption(option),
                    'border-3 outline-black rounded-full border-white  p-1':
                      selectedOptions &&
                      selectedOptions[attribute?.attribute_code || ''] &&
                      selectedOptions[attribute?.attribute_code || ''] === option.uid,
                  }"
                  :aria-label="$t('select', { label: attribute.label })"
                  :disabled="!activeOption(option)"
                  @click="handleOnSelectOption(attribute?.attribute_code || '', option?.uid || '')"
                />
              </div>

              <AppButton
                v-else
                as="button"
                :disabled="!activeOption(option)"
                :with-arrow="false"
                :inverted="
                  !!(
                    selectedOptions &&
                    selectedOptions[attribute?.attribute_code || ''] &&
                    selectedOptions[attribute?.attribute_code || ''] === option.uid
                  )
                "
                disable-hover
                :variant="
                  selectedOptions &&
                  selectedOptions[attribute?.attribute_code || ''] &&
                  selectedOptions[attribute?.attribute_code || ''] === option.uid
                    ? 'black'
                    : 'outline'
                "
                @click="handleOnSelectOption(attribute?.attribute_code || '', option?.uid || '')"
              >
                <span class="text-sm"> {{ option.label }} </span>
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductContext } from '@robustastudio/e-commerce/common';

defineComponent({
  name: 'ProductVariantSelector',
});

const props = defineProps({
  productVariants: {
    type: Array as PropType<NonNullable<ProductContext['variants']>>,
    default: () => [],
  },
});

const { setProductConfiguration } = usesSetProductConfiguration();
setProductConfiguration('fist-only');

const { productOptions, selectedOptions } = useProductConfigurationOptions();

function handleOnSelectOption(optionCode: string, value: string) {
  // if (selectedOptions.value?.[optionCode] && selectedOptions.value?.[optionCode] === value) {
  //   selectedOptions.value = omit(selectedOptions.value, optionCode);

  //   return;
  // }
  selectedOptions.value = { ...selectedOptions.value, [optionCode]: value };
}

/**
 * @param option: product option
 * loop over product variants and find at least one variant that have same passed option uid and the variant it self in stock
 * - if result is true, that means this option will be in stock for another variant
 */
function activeOption(option: NonNullable<NonNullable<typeof productOptions.value>[number]['values']>[number]) {
  const variantStock = props.productVariants.some(
    (variant: any) => variant.attributes?.some((attr: any) => attr?.uid === option.uid) && variant.stock,
  );

  return variantStock && option.active;
}
</script>

<style lang="postcss" scoped>
:deep(.AppButton) {
  @apply flex items-center rounded-lg;
  height: 37px;
}

a {
  @apply focus:outline-none;

  &.nuxt-link-exact-active {
    @apply bg-secondary-200;
  }
}

.VariantsContainer {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  column-gap: 10px;
  row-gap: 8px;
}

.disabled-option {
  @apply opacity-50;
  &::after {
    @apply bg-black;
    content: '';
    width: 4px;
    height: 39px;
    z-index: 10;
    position: absolute;
    top: -3px;
    left: 12px;

    transform: rotate(45deg);
  }
}

.outline-black {
  outline: 1px solid black;
}
</style>

<i18n>
  {
    "en": {
      "choose": "Choose {label} : {value} ",
      "sizeGuide": "Size Guide",
      "select": "Select {label}"
    },
    "ar": {
      "choose": "اختر {label} : {value} ",
      "sizeGuide": "دليل المقاسات",
      "select": "اختر {label}"
    }
  }
</i18n>
